import axiosInstance from '../axios.config';
import type { CartLineItem } from '../../store/cart/cart.slice';

interface CartItemsResponse {
  items?: CartLineItem[];
}

interface CartAuthConfig {
  token?: string | null;
  nonce?: string | null;
}

const CART_ITEMS_ENDPOINT = '/nex/v1/sync-cart';

const getCartItemsConfig = ({ token, nonce }: CartAuthConfig = {}) => ({
  headers: {
    ...(token && token !== 'null' ? { Authorization: `Bearer ${token}` } : {}),
    ...(nonce && nonce !== 'null' ? { 'X-WP-Nonce': nonce } : {}),
    'Content-Type': 'application/json',
  },
  withCredentials: true,
});

const normalizeCartItemsResponse = (data: unknown): CartLineItem[] => {
  if (Array.isArray(data)) {
    return data as CartLineItem[];
  }

  if (data && typeof data === 'object' && Array.isArray((data as CartItemsResponse).items)) {
    return (data as CartItemsResponse).items as CartLineItem[];
  }

  return [];
};

export const fetchCartItemsFromServer = async (token: string, nonce?: string): Promise<CartLineItem[]> => {
  if ((!token || token === 'null') && (!nonce || nonce === 'null')) {
    return [];
  }

  const response = await axiosInstance.get<CartLineItem[] | CartItemsResponse>(
    CART_ITEMS_ENDPOINT,
    getCartItemsConfig({ token, nonce })
  );

  return normalizeCartItemsResponse(response.data);
};

export const saveCartItemsToServer = async (
  items: CartLineItem[],
  token: string,
  nonce?: string
): Promise<CartLineItem[]> => {
  if ((!token || token === 'null') && (!nonce || nonce === 'null')) {
    return items;
  }

  const response = await axiosInstance.post<CartLineItem[] | CartItemsResponse>(
    CART_ITEMS_ENDPOINT,
    items,
    getCartItemsConfig({ token, nonce })
  );

  const normalizedItems = normalizeCartItemsResponse(response.data);
  return normalizedItems.length > 0 ? normalizedItems : items;
};
